import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaFacebookF, 
  FaTwitter, 
  FaInstagram, 
  FaLinkedinIn, 
  FaYoutube,
  FaPaperPlane,
  FaArrowRight
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import logo from '../../assets/logo.png';

interface NavLink {
  name: string;
  path: string;
  icon?: React.ReactNode;
}

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
}

const Footer: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const currentYear = new Date().getFullYear();

  const companyInfo = {
    description: 'Leading provider of solar energy solutions, committed to sustainable and efficient power generation for a greener future.'
  };

  const quickLinks: NavLink[] = [
    { name: 'About', path: '/about' },
    { name: 'Packages', path: '/packages' },
    { name: 'Project Photos', path: '/project-photos' },
    { name: 'Project Videos', path: '/project-videos' },
  ];

  const services: NavLink[] = [
    { name: 'Brochures', path: '/brochures' },
    { name: 'Blog', path: '/events' },
    { name: 'Inquiry', path: '/inquiry' },
    { name: 'Contact', path: '/contact' },
  ];

  const socialLinks = [
    { icon: <FaFacebookF />, path: '#', label: 'Facebook' },
    { icon: <FaTwitter />, path: '#', label: 'Twitter' },
    { icon: <FaInstagram />, path: '#', label: 'Instagram' },
    { icon: <FaLinkedinIn />, path: '#', label: 'LinkedIn' },
    { icon: <FaYoutube />, path: '#', label: 'YouTube' }
  ] as const;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: ContactFormData) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Here you would typically send the form data to your backend
      console.log('Form submitted:', formData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsSuccess(true);
      setFormData({ name: '', email: '', phone: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderLinks = (title: string, links: NavLink[]) => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      <ul className="space-y-3">
        {links.map((link) => (
          <motion.li 
            key={link.path}
            whileHover={{ x: 5 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <Link
              to={link.path}
              className="flex items-center text-gray-400 hover:text-blue-400 transition-colors group"
            >
              <FaArrowRight className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
              <span>{link.name}</span>
            </Link>
          </motion.li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-gray-900/95 text-gray-300 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiPjxkZWZzPjxwYXR0ZXJuIGlkPSJwYXR0ZXJuIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHBhdHRlcm5UcmFuc2Zvcm09InJvdGF0ZSg0NSkiPjxyZWN0IHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNwYXR0ZXJuKSIvPjwvc3ZnPg==')]">
        </div>
      </div>
      
      <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-20 xl:px-28 2xl:px-40 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8">
          {/* Company Info */}
          <div className="space-y-6 lg:col-span-4">
            <Link to="/" className="inline-block">
              <div className="flex items-center space-x-2">
                <img 
                  src={logo} 
                  alt="SS Solar Logo" 
                  className="h-12 w-auto object-contain"
                />
                <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  SS Solar
                </span>
              </div>
            </Link>
            <p className="text-gray-400 leading-relaxed">
              {companyInfo.description}
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-all"
                  whileHover={{ y: -3, scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label={social.label}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            {renderLinks('Quick Links', quickLinks)}
          </div>
          
          {/* Services */}
          <div className="lg:col-span-2">
            {renderLinks('Our Services', services)}
          </div>

          {/* Quick Contact Form - Right aligned */}
          <div className="space-y-6 lg:col-span-3 lg:col-start-10 lg:col-end-13">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Quick Contact</h3>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    className="w-full px-4 py-2.5 rounded-lg bg-gray-800/50 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm text-white placeholder-gray-500 transition-all"
                    required
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    className="w-full px-4 py-2.5 rounded-lg bg-gray-800/50 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm text-white placeholder-gray-500 transition-all"
                    required
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Your Phone"
                    className="w-full px-4 py-2.5 rounded-lg bg-gray-800/50 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm text-white placeholder-gray-500 transition-all"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-2.5 ${isSubmitting ? 'bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'} text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <span>Send Message</span>
                      <FaPaperPlane className="w-3.5 h-3.5" />
                    </>
                  )}
                </button>
                {isSuccess && (
                  <div className="mt-2 text-sm text-green-400 text-center">
                    Thank you! We'll get back to you soon.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-800 my-12"></div>

        {/* Copyright */}
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 text-center md:text-left">
          <p className="text-sm text-gray-500">
            &copy; {currentYear} SS Solar. All rights reserved.
          </p>
          <div className="flex items-center space-x-6">
            <Link to="/privacy-policy" className="text-sm text-gray-400 hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-sm text-gray-400 hover:text-white transition-colors">
              Terms of Service
            </Link>
            <div className="text-sm text-gray-500">
              Developed by{''}
              <a 
                href="https://codrex.in" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 transition-colors"
              >
                Codrex Digital
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
